import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import {
  useWaitingRoomFilters,
  useWaitingRoomFiltersContext,
} from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import { Service } from "@/types/__generated__/union-types"

const SERVICES: Service[] = [
  "CONSULTATION",
  "PREVENTION_ASSESSMENT",
  "KIDS_LINE",
  "EMERGENCY_LINE",
  "MEDGATE_CONNECT",
  "PSYCHOSOCIAL",
  "STUDY_CALL",
  "ADMINISTRATIVE_REQUEST",
  "TELE_GENERAL_PRACTITIONER",
]

const ServicesFilter = () => {
  const { t } = useTranslation()
  const { filters } = useWaitingRoomFilters()
  const setFilters = useWaitingRoomFiltersContext((state) => state.setFilters)

  const activeFilters = filters.services || []
  const activeFilterCount = activeFilters.length

  const handleCheckChange = (item: Service) => {
    const index = activeFilters.indexOf(item)
    const newActiveFilters = [...activeFilters]

    if (index < 0) newActiveFilters.push(item)
    else newActiveFilters.splice(index, 1)

    if (newActiveFilters.length > 0) setFilters({ services: newActiveFilters })
    else setFilters({ services: null })
  }

  const handleResetServices = () => {
    setFilters({ services: null })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        className="aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer">
        <Chip
          data-testid="services-filter-trigger"
          className="h-8 gap-0.5"
          checked={activeFilterCount > 0}
          type="button">
          <div
            className={cn("hidden w-5 overflow-hidden transition-all duration-200", activeFilterCount > 0 && "block")}>
            <Check size={20} />
          </div>
          <span className="px-1.5">{`${t("scheduling.waitingRoom.filters.services")}${activeFilterCount > 0 ? ` (${activeFilterCount})` : ``}`}</span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="services-filter-content"
        className="absolute -top-1 grid gap-0.5 rounded-md border-none p-2 leading-5">
        {SERVICES.map((service) => (
          <DropdownMenuCheckboxItem
            key={service}
            onSelect={(e) => e.preventDefault()}
            className="h-9"
            checked={activeFilters.includes(service)}
            data-testid={`services-filter-${service.toLowerCase()}`}
            onCheckedChange={() => handleCheckChange(service)}>
            {t(`scheduling.waitingRoom.service.${service}`)}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator className="bg-border -mx-2 my-2" />
        <div className="flex justify-end">
          <Button
            onClick={handleResetServices}
            data-testid="reset-services-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export { ServicesFilter }
